import elrondHelper, { WALLET_LOGIN_TYPE } from "@/helpers/elrond";
import storageHelper from "@/helpers/storage";
import elrondApiHelper, { MEME_NFT_POST, SQUIRREL_NFT_POST } from "@/helpers/elrondApi";
import storeHelper, { LIST } from "@/helpers/store";
import { Account, TokenTransfer, TransactionWatcher } from "@multiversx/sdk-core";
import { WalletConnectV2Provider } from "@multiversx/sdk-wallet-connect-provider";
import { HWProvider } from "@multiversx/sdk-hw-provider";
import ELROND from "@/constants/elrond";
import { REFERRAL_MUTATIONS } from "@/store/referral";
import { PATHS_EN, ROUTES } from "@/router/constants";
import BLOCKCHAIN from "@/constants/blockchain";
import GENERAL from "@/constants/general";
import { ExtensionProvider } from "@multiversx/sdk-extension-provider/out";
import authClient from "@/api/auth";
import generalClient from "@/api/general";
import memeClient from "@/api/meme";
import { NFT_POST_VIEW } from "@/store/meme";
import avatarHelper from "@/helpers/avatar";

export const USER_GETTERS = {
  ADDRESS_ELROND: "userAddressElrond",
  ACCOUNT_ELROND: "userAccountElrond",
  NFTS: "userNFTs",
  NEXT_GEN_NFTS: "userNextGenNFTs",
  CARD_NFTS: "userCardNFTs",
  TOKENS: "userTokens",
  AVATAR: "userAvatar",
  USERNAME: "userUsername",
  BANNER: "userBanner",
  MEMES: "userMemes",
};

export const USER_MUTATIONS = {
  ADDRESS_ELROND: "userAddressElrond",
  ACCOUNT_ELROND: "userAccountElrond",
  NFTS: "userNFTs",
  NEXT_GEN_NFTS: "userNextGenNFTs",
  CARD_NFTS: "userCardNFTs",
  TOKENS: "userTokens",
  TOKEN_UPDATE: "userTokenUpdate",
  AVATAR: "userAvatar",
  USERNAME: "userUsername",
  BANNER: "userBanner",
  MEMES: "userMemes",
  MEMES_MODIFY: "userMemesModify",
  SET_ADDRESS_LAST_MEME_TIME: "userSetAddressLastMemeTime",
};

export const USER_ACTIONS = {
  LOGIN_RECHECK: "userLoginRecheck",
  LOGIN_MAIAR: "userLoginMaiar",
  POST_LOGIN_MAIAR: "userPostLoginMaiar",
  LOGIN_EXTENSION: "userLoginExtension",
  LOGIN_LEDGER: "userLoginLedger",
  POST_LOGIN_LEDGER: "userPostLoginLedger",
  LOGIN_WEB_WALLET: "userLoginWebWallet",
  POST_LOGIN_WEB_WALLET: "userPostLoginWebWallet",
  LOGIN_HUB: "userLoginHub",
  LOGOUT_ELROND: "userLogoutElrond",

  BACKEND_GET_LOGIN_TOKEN: "userBackendGetLoginToken",

  RELOAD_ACCOUNT: "userReloadAccount",

  LOGIN_BACKEND: "userLoginBackend",

  GET_NFTS: "userGetNfts",
  GET_NEXT_GEN_NFTS: "userGetNextGenNFTs",
  GET_TOKEN_BALANCE: "userGetTokenBalance",
  SET_AVATAR: "userSetAvatar",
  SET_USERNAME: "userSetUsername",
  SET_BANNER: "userSetBanner",

  GET_ADDRESS_LAST_MEME_TIME: "userGetAddressLastMemeTime",

  GET_WALLET_MEMES: "userGetWalletMemes",
  GET_WALLET_TOP_MEMES: "userGetWalletTopMemes",
};

interface IUserStore {
  state: () => {
    addressElrond: string | boolean;
    accountElrond?: Account;
    nfts?: LIST<SQUIRREL_NFT_POST>;
    nextGenNfts?: LIST<SQUIRREL_NFT_POST>;
    cardNfts?: LIST<SQUIRREL_NFT_POST>;
    tokens: { [key: string]: TokenTransfer };
    avatar: string | null;
    username: string | null;
    banner: string | null;

    memes?: LIST<MEME_NFT_POST>;
  };

  [index: string]: any;
}

export const userStore: IUserStore = {
  state: () => ({
    addressElrond: false,
    accountElrond: null,
    nfts: null,
    nextGenNfts: null,
    cardNfts: null,
    tokens: {},
    avatar: null,
    username: null,
    banner: null,
  }),
  getters: {
    [USER_GETTERS.ADDRESS_ELROND](state) {
      return state.addressElrond;
    },
    [USER_GETTERS.ACCOUNT_ELROND](state) {
      return state.accountElrond;
    },
    [USER_GETTERS.NFTS](state) {
      return state.nfts;
    },
    [USER_GETTERS.NEXT_GEN_NFTS](state) {
      return state.nextGenNfts;
    },
    [USER_GETTERS.CARD_NFTS](state) {
      return state.cardNfts;
    },
    [USER_GETTERS.TOKENS](state) {
      return state.tokens;
    },
    [USER_GETTERS.AVATAR](state) {
      return state.avatar;
    },
    [USER_GETTERS.USERNAME](state) {
      return state.username;
    },
    [USER_GETTERS.BANNER](state) {
      return state.banner;
    },
    [USER_GETTERS.MEMES](state) {
      return state.memes;
    },
  },
  mutations: {
    [USER_MUTATIONS.ADDRESS_ELROND](state, address) {
      state.addressElrond = address;
    },
    [USER_MUTATIONS.ACCOUNT_ELROND](state, account) {
      state.accountElrond = account;
    },
    [USER_MUTATIONS.NFTS](state, nfts) {
      state.nfts = nfts;
    },
    [USER_MUTATIONS.NEXT_GEN_NFTS](state, nextGenNfts) {
      state.nextGenNfts = nextGenNfts;
    },
    [USER_MUTATIONS.CARD_NFTS](state, cardNfts) {
      state.cardNfts = cardNfts;
    },
    [USER_MUTATIONS.TOKENS](state, tokens) {
      state.tokens = tokens;
    },
    [USER_MUTATIONS.TOKEN_UPDATE](state, { token, balance }) {
      state.tokens[token] = balance;
    },
    [USER_MUTATIONS.AVATAR](state, avatar) {
      state.avatar = avatar;
    },
    [USER_MUTATIONS.USERNAME](state, username) {
      state.username = username;
    },
    [USER_MUTATIONS.BANNER](state, banner) {
      state.banner = banner;
    },
    [USER_MUTATIONS.MEMES](state, memes) {
      state.memes = memes;
    },
    [USER_MUTATIONS.MEMES_MODIFY](state, newMeme) {
      state.memes = storeHelper.editList(state.memes, newMeme);
    },
    [USER_MUTATIONS.SET_ADDRESS_LAST_MEME_TIME](_, time) {
      storageHelper.setAddressLastMemeTime(time);
    },
  },
  actions: {
    async [USER_ACTIONS.LOGIN_RECHECK]({ dispatch }) {
      const query = new URLSearchParams(window.location.search);

      if (query.has("accessToken")) {
        await dispatch(USER_ACTIONS.LOGIN_HUB, { accessToken: query.get("accessToken") });

        return;
      }

      // Also require backend token to be set
      if (!storageHelper.getBackendAccessToken()) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return;
      }

      let initialAddress = storageHelper.getWalletLogin();
      if (null !== initialAddress) {
        await dispatch(USER_ACTIONS.POST_LOGIN_WEB_WALLET, initialAddress);

        return;
      }

      // Check if there is an extension login address saved
      initialAddress = storageHelper.getExtensionLogin();
      if (initialAddress) {
        await dispatch(USER_ACTIONS.LOGIN_EXTENSION, initialAddress);

        return;
      }

      // Check if there is a Maiar login address saved
      initialAddress = storageHelper.getMaiarLogin();
      if (initialAddress) {
        await dispatch(USER_ACTIONS.LOGIN_MAIAR, {
          handleOnLogin: () => dispatch(USER_ACTIONS.POST_LOGIN_MAIAR, true),
          handleOnLogout: () => dispatch(USER_ACTIONS.LOGOUT_ELROND),
          isRecheck: true,
        });

        return;
      }

      initialAddress = storageHelper.getLedgerLogin();
      if (initialAddress !== null) {
        try {
          await dispatch(USER_ACTIONS.LOGIN_LEDGER, initialAddress);

          return;
        } catch (e) {
          console.error(e);
        }
      }

      dispatch(USER_ACTIONS.LOGOUT_ELROND);
    },

    async [USER_ACTIONS.LOGIN_MAIAR]({ dispatch }, { handleOnLogin, handleOnLogout, isRecheck = false }) {
      const initialized = await elrondHelper.initializeMaiar(handleOnLogin, handleOnLogout);

      if (!initialized) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);
        alert("Something went wrong...");

        return null;
      }

      if (isRecheck) {
        // const pairings = await (elrondHelper.provider as WalletConnectV2Provider).getPairings();
        //
        // console.log('pairings', pairings);

        return true;
      }

      const url = await elrondHelper.login("", storageHelper.getBackendLoginToken(), false, false);

      return url;
    },

    async [USER_ACTIONS.POST_LOGIN_MAIAR]({ commit, dispatch }, isRecheck = false) {
      const address = await (elrondHelper.provider as WalletConnectV2Provider).getAddress();

      if (!address) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);
        alert("Something went wrong...");

        return null;
      }

      if (!isRecheck) {
        const signature = await (elrondHelper.provider as WalletConnectV2Provider).getSignature();

        await dispatch(USER_ACTIONS.LOGIN_BACKEND, { address, signature });
      }

      commit(USER_MUTATIONS.ADDRESS_ELROND, address);
      storageHelper.setMaiarLogin(address);
      await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);

      return address;
    },

    async [USER_ACTIONS.LOGIN_EXTENSION]({ commit, dispatch }, initialAddress) {
      const initialized = await elrondHelper.initializeExtension(initialAddress);

      if (!initialized) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);
        alert("Extension not installed...");

        return null;
      }

      let address = initialAddress;
      if (!initialAddress) {
        try {
          address = await elrondHelper.login("", storageHelper.getBackendLoginToken());
        } catch (e) {
          // Error handled further down
        }
      }

      if (!address) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return null;
      }

      if (!initialAddress) {
        const signature = (elrondHelper.provider as ExtensionProvider).getAccount().signature;

        await dispatch(USER_ACTIONS.LOGIN_BACKEND, { address, signature });
      }

      commit(USER_MUTATIONS.ADDRESS_ELROND, address);
      storageHelper.setExtensionLogin(address);
      await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);

      return address;
    },

    async [USER_ACTIONS.LOGIN_LEDGER]({ dispatch }, initialAddress = null) {
      const initialized = await elrondHelper.initializeLedger();

      if (!initialized) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return false;
      }

      if (initialAddress) {
        return await dispatch(USER_ACTIONS.POST_LOGIN_LEDGER, initialAddress);
      }

      return await (elrondHelper.provider as HWProvider).getAccounts();
    },
    // TODO: Check ledger login works with backend
    async [USER_ACTIONS.POST_LOGIN_LEDGER](
      { dispatch, commit },
      { addressIndex, securityToken = null, address = null },
    ) {
      if (!address) {
        let signature: Buffer = null;

        // @ts-ignore
        // @ts-ignore
        ({ address, signature } = await elrondHelper.login("", securityToken, addressIndex));

        if (address) {
          await dispatch(USER_ACTIONS.LOGIN_BACKEND, { address, signature: signature.toString("hex") });
        }
      } else {
        await elrondHelper.login("", storageHelper.getBackendLoginToken(), addressIndex, true);
      }

      if (!address) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);
        alert("Something went wrong...");

        return null;
      }

      commit(USER_MUTATIONS.ADDRESS_ELROND, address);
      storageHelper.setLedgerLogin(addressIndex, address);
      await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);

      return address;
    },
    async [USER_ACTIONS.LOGIN_WEB_WALLET](_, { redirect, type }) {
      await elrondHelper.initializeWebWallet(type);

      await storageHelper.setWalletLogin();
      await storageHelper.setWalletLoginType(type);

      await elrondHelper.login(redirect || PATHS_EN[ROUTES.home], storageHelper.getBackendLoginToken());
    },
    async [USER_ACTIONS.POST_LOGIN_WEB_WALLET]({ commit, dispatch }, address) {
      const type = storageHelper.getWalletLoginType() || WALLET_LOGIN_TYPE.WEB_WALLET;
      await elrondHelper.initializeWebWallet(type);

      // Check if we are in callback from Web Wallet and get address from URL
      if (!address) {
        const query = new URLSearchParams(window.location.search);

        if (query.has("address")) {
          address = query.get("address");
        }

        if (query.has("signature")) {
          const signature = query.get("signature");

          await dispatch(USER_ACTIONS.LOGIN_BACKEND, { address, signature });
        }

        const newQuery = Object.fromEntries(new URLSearchParams(window.location.search).entries());

        delete newQuery.address;
        delete newQuery.signature;

        if (address) {
          try {
            const router = (await import("@/router")).default;
            router.replace({ path: window.location.pathname, query: newQuery });
          } catch (e) {
            // Do nothing
          }
        }
      }

      if (!address) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return null;
      }

      commit(USER_MUTATIONS.ADDRESS_ELROND, address);
      storageHelper.setWalletLogin(address);
      await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);

      return address;
    },
    // TODO: Check Hub login works with backend
    async [USER_ACTIONS.LOGIN_HUB]({ commit, dispatch }, { accessToken }) {
      const result = elrondHelper.decodeNativeAuthToken(accessToken);

      if (!result) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return null;
      }

      const initialized = await elrondHelper.initializeWebView(result, accessToken);

      if (!initialized) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return null;
      }

      // await elrondHelper.login();

      let address = result.address;

      if (!address) {
        dispatch(USER_ACTIONS.LOGOUT_ELROND);

        return null;
      }

      await dispatch(USER_ACTIONS.LOGIN_BACKEND, { address, signature: result.signature });

      commit(USER_MUTATIONS.ADDRESS_ELROND, address);
      storageHelper.setWebViewLogin(address);
      await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);

      return address;
    },

    [USER_ACTIONS.LOGOUT_ELROND]({ commit, getters }) {
      elrondHelper.logout();

      storageHelper.clearLogins(true, getters[USER_GETTERS.ADDRESS_ELROND]);

      commit(USER_MUTATIONS.ADDRESS_ELROND, null);
      commit(USER_MUTATIONS.ACCOUNT_ELROND, null);
      commit(USER_MUTATIONS.TOKENS, {});

      commit(REFERRAL_MUTATIONS.RESET);

      // Need actual string here, because of circular references
      commit("resetTransactionToast");
    },

    async [USER_ACTIONS.BACKEND_GET_LOGIN_TOKEN]() {
      const token = await authClient.authToken();

      storageHelper.setBackendLoginToken(token);
    },

    async [USER_ACTIONS.RELOAD_ACCOUNT]({ commit, getters }) {
      const account = await elrondHelper.getAccount(getters[USER_GETTERS.ADDRESS_ELROND]);

      if (getters[USER_GETTERS.ACCOUNT_ELROND]) {
        getters[USER_GETTERS.ACCOUNT_ELROND].update(account);

        return;
      }

      commit(USER_MUTATIONS.ACCOUNT_ELROND, account);

      const savedAvatar = storageHelper.getItem('user_avatar');
      if (savedAvatar) {
        commit(USER_MUTATIONS.AVATAR, savedAvatar);
      }
    },

    async [USER_ACTIONS.LOGIN_BACKEND]({ commit, getters, dispatch }, { address, signature }) {
      const token = storageHelper.getBackendLoginToken();

      if (!token) {
        await dispatch(USER_ACTIONS.LOGOUT_ELROND);

        alert("Backend login failed...");
        throw new Error("Backend login token missing");
      }

      try {
        const accessToken = await authClient.authLogin(address, token, signature);

        storageHelper.setBackendAccessToken(accessToken);
      } catch (e) {
        await dispatch(USER_ACTIONS.LOGOUT_ELROND);

        alert("Backend login failed...");
        throw new Error("Backend login failed");
      }
    },

    async [USER_ACTIONS.GET_NFTS]({ commit, getters }, { page, collection, mutation, perPage = GENERAL.PER_PAGE }) {
      const address = getters[USER_GETTERS.ADDRESS_ELROND];

      const total = await elrondApiHelper.getAddressNftsCount(address, collection);
      const nfts = await elrondApiHelper.getAddressNfts(address, collection, page, total, perPage);

      if (0 === page) {
        storeHelper.commitList(commit, mutation, nfts, page, total);

        return;
      }

      const oldNfts = getters[mutation]?.items;
      const newNfts = [...oldNfts, ...nfts];

      storeHelper.commitList(commit, mutation, newNfts, page, total);
    },
    async [USER_ACTIONS.GET_TOKEN_BALANCE](
      { commit, dispatch, getters },
      { token, decimals = null, force = false, transaction = null },
    ) {
      if (!getters[USER_GETTERS.ACCOUNT_ELROND]) {
        return null;
      }

      if (token in getters[USER_GETTERS.TOKENS] && !force) {
        return getters[USER_GETTERS.TOKENS][token];
      }

      if (!decimals) {
        decimals = BLOCKCHAIN.TOKENS[token]?.decimals;
      }

      if (transaction) {
        const transactionWatcher = new TransactionWatcher(elrondApiHelper.apiProvider, {
          timeoutMilliseconds: TransactionWatcher.DefaultTimeout * 2,
        });
        await transactionWatcher.awaitCompleted(transaction);
      }

      if (ELROND.EGLD_TOKEN === token) {
        if (force) {
          await dispatch(USER_ACTIONS.RELOAD_ACCOUNT);
        }

        const balance = TokenTransfer.egldFromBigInteger(getters[USER_GETTERS.ACCOUNT_ELROND].balance);

        commit(USER_MUTATIONS.TOKEN_UPDATE, { token, balance });

        return balance;
      }

      try {
        const tokenBalance = await elrondApiHelper.getAccountTokenBalance(getters[USER_GETTERS.ADDRESS_ELROND], token);

        const balance = TokenTransfer.fungibleFromBigInteger(tokenBalance.identifier, tokenBalance.balance, decimals);

        commit(USER_MUTATIONS.TOKEN_UPDATE, { token, balance });

        return balance;
      } catch (e) {
        commit(USER_MUTATIONS.TOKEN_UPDATE, {
          token,
          balance: TokenTransfer.fungibleFromBigInteger(token, 0, decimals),
        });

        return null;
      }
    },

    async [USER_ACTIONS.SET_AVATAR]({ commit, getters }, avatar) {
      commit(USER_MUTATIONS.AVATAR, avatar);

      // Save the avatar to local storage for persistence
      storageHelper.setItem("user_avatar", avatar);

      // Also save in the avatar helper with the user's address
      const address = getters[USER_GETTERS.ADDRESS_ELROND];
      if (address) {
        avatarHelper.setAvatarForAddress(address, avatar);
      }
    },
    async [USER_ACTIONS.SET_USERNAME]({ commit, getters }, username) {
      try {
        // Save to local storage
        storageHelper.setUserUsername(username);

        // Update state
        commit(USER_MUTATIONS.USERNAME, username);

        // Store the username-address mapping if address exists
        const address = getters[USER_GETTERS.ADDRESS_ELROND];
        if (address && typeof address === "string") {
          avatarHelper.setUsernameForAddress(address, username);
        }

        // Optionally, save to backend
        // await api.saveUsername(username);

        return true;
      } catch (error) {
        console.error("Error setting username", error);
        return false;
      }
    },
    async [USER_ACTIONS.SET_BANNER]({ commit }, banner) {
      try {
        // Save to local storage
        storageHelper.setUserBanner(banner);

        // Update state
        commit(USER_MUTATIONS.BANNER, banner);

        // Optionally, save to backend
        // await api.saveBanner(banner);

        return true;
      } catch (error) {
        console.error("Error setting banner", error);
        return false;
      }
    },

    async [USER_ACTIONS.GET_ADDRESS_LAST_MEME_TIME]({ commit }, addressElrond) {
      let time = storageHelper.getAddressLastMemeTime();

      if (!time) {
        time = await generalClient.addressLastMemeTime(addressElrond);

        commit(USER_MUTATIONS.SET_ADDRESS_LAST_MEME_TIME, time);
      }

      return time;
    },
    async [USER_ACTIONS.GET_WALLET_MEMES]({ commit, getters }, { address, page }) {
      const total = await elrondApiHelper.getAddressNftsCount(address, BLOCKCHAIN.MEME_NFT_TOKEN_IDENTIFIER);
      const response = await elrondApiHelper.getAddressMemeNfts(address, page, total);

      await commitMemeListWithAllInfo(response, page, commit, total, getters[USER_GETTERS.MEMES]?.items);
    },
    async [USER_ACTIONS.GET_WALLET_TOP_MEMES]({ commit, getters }, { address, page }) {
      const total = await elrondApiHelper.getAddressNftsCount(address, BLOCKCHAIN.TOP_MEME_NFT_TOKEN_IDENTIFIER);
      const response = await elrondApiHelper.getAddressMemeNfts(
        address,
        page,
        total,
        BLOCKCHAIN.TOP_MEME_NFT_TOKEN_IDENTIFIER,
      );

      await commitMemeListWithAllInfo(response, page, commit, total, getters[USER_GETTERS.MEMES]?.items);
    },
  },
};

async function commitMemeListWithAllInfo(response: MEME_NFT_POST[], page, commit, total: number, oldMemes) {
  const allNonces = response.map((nft) => nft.nonce);
  const nftsVotes = await memeClient.memesVotesAll(allNonces);
  const nftsRarity = await memeClient.memesRarity(allNonces);

  const memes = response.map((meme): NFT_POST_VIEW => {
    const votes = nftsVotes[meme.nonce];
    const actualRarity = nftsRarity[meme.nonce];

    return {
      ...meme,
      ...votes,
      actualRarity,
    };
  });

  if (0 === page) {
    storeHelper.commitList(commit, USER_MUTATIONS.MEMES, memes, page, total);

    return;
  }

  const newMemes = [...oldMemes, ...memes];

  storeHelper.commitList(commit, USER_MUTATIONS.MEMES, newMemes, page, total);
}
